import { useMemo, useState, useEffect } from "react";
import apiService, { handleError } from "../../Common/ApiCommon/ApiService";
import icons from "../../../assets/images/constantIcons";
import DeletePopup from "./DeletePopup";
import EditExpenseForm from "./EditExpenseForm";
import toast from "react-hot-toast";

const useAddExpense = () => {
  const [editPopupIndex, setEditPopupIndex] = useState(null);
  const [deletePopupIndex, setDeletePopupIndex] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [expense, setExpense] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cashIn, setCashIn] = useState(0);
  const [cashOut, setCashOut] = useState(0);
  const [netBalance, setNetBalance] = useState(0);
  const [filters, setFilters] = useState({
    filter: "",
    from: "",
    to: "",
    entry_type: "",
    created_by_id: "",
    party_id: "",
    category_id: "",
    payment_mode: [],
    search: ""
  });



  // ...

  const handleEditPopup = (id) => {
    const expenseItem = expense.find((item) => item.id === id);
    if (expenseItem?.party?.name === "Booking") {
      toast.error("You cannot edit a Booking entry.");
      return;
    }
    setEditPopupIndex(id);
  };

  const handleDeletePopup = (id) => {
    const expenseItem = expense.find((item) => item.id === id);
    if (expenseItem?.party?.name === "Booking") {
      toast.error("You cannot delete a Booking entry.");
      return;
    }
    setDeletePopupIndex(id);
  };

  const fetchExpenses = async () => {
    setLoading(true);
    try {
      let queryParams = [];

      if (filters.filter) {
        queryParams.push(`filter=${filters.filter}`);
      }

      // custom date range only when 'custom' is selected
      if (filters.from && filters.filter === "custom") {
        queryParams.push(`from=${filters.from}`);
      }
      if (filters.to && filters.filter === "custom") {
        queryParams.push(`to=${filters.to}`);
      }


      if (filters.entry_type) {
        queryParams.push(`entry_type=${filters.entry_type}`);
      }
      if (filters.created_by_id) {
        queryParams.push(`created_by_id=${filters.created_by_id}`);
      }
      if (filters.party_id) {
        queryParams.push(`party_id=${filters.party_id}`);
      }
      // if (filters.category_id) {
      //   queryParams.push(`category_id=${filters.category_id}`);
      // }
      if (filters.payment_mode.length) {
        filters.payment_mode.forEach((mode) => {
          queryParams.push(`payment_mode[]=${mode}`);
        });
      }
      if (filters.search) {
        queryParams.push(`search=${filters.search}`);
      }

      const queryString = queryParams.join("&");
      const response = await apiService.get(`/expenses?${queryString}`);

      setExpense(response.data);
      setCashIn(response?.cash_in);
      setCashOut(response?.cash_out);
      setNetBalance(response?.net_balance);

    } catch (errors) {
      handleError(errors);
    } finally {
      setLoading(false);
    }
  };

  const updateFilter = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };
  useEffect(() => {
    fetchExpenses();
  }, [filters]);

  const clearFilters = () => {
    setFilters({
      filter: "",
      from: "",
      to: "",
      entry_type: "",
      created_by_id: "",
      party_id: "",
      // category_id: "",
      payment_mode: [],
      search: ""
    });
  };

  const columns = useMemo(
    () => [
      {
        Header: "Date & Time",
        accessor: "date",
        Cell: ({ row, value }) => {
          const timeValue = row.original.time;
          const dateObj = new Date(timeValue);
          const formattedTime = dateObj.toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          });

          return (
            <div className="text-nowrap">
              <div>{value}</div>
              <div className="text-gray-500 text-sm">{formattedTime}</div>
            </div>
          );
        },
      },

      {
        Header: "Details",
        accessor: "details",
        headerAlign: "text-left",
        cellAlign: "text-center",
        Cell: ({ row }) => {
          const partyName = row.original.party?.name || "-";
          const detailsText = row.original.details || "-";

          return (
            <div className="flex flex-col items-start md:items-center">
              <div className="flex">
                <div className="font-medium">({partyName})</div>
                {/* <div className="text-gray-500 text-sm">({partyType})</div> */}
              </div>
              <div className="">{detailsText}</div>
            </div>
          )
        },
      },
      // {
      //   Header: "CATEGORY",
      //   accessor: "expense_category",
      //   Cell: ({ row }) => {
      //     const categoryName = row.original.expense_category?.name || "-";
      //     return <span>{categoryName}</span>;
      //   },
      // },

      {
        Header: "Mode",
        accessor: "payment_mode",
      },
      // {
      //   Header: "Bill",
      //   accessor: "files",
      //   Cell: ({ value }) => (
      //     value && value.length > 0 ? (
      //       <a
      //         href={`https://headquater.pixoatic.com${value[0]}`}
      //         target="_blank"
      //         rel="noopener noreferrer"
      //         className="text-blue underline"
      //       >
      //         view bill
      //       </a>
      //     ) : (
      //       <span className="text-gray-500">-</span>
      //     )
      //   )
      // },
      {
        Header: "AMOUNT",
        accessor: "amount",
        Cell: ({ row }) => {
          const amount = row.original.amount;
          const entryType = row.original.entry_type;

          return (
            <span className={`${entryType === "Cash Out" ? "text-red" : "text-green"}`}>
              {`₹ ${Math.trunc(amount || "-")}`}
            </span>
          );
        },
      },

      {
        Header: "Balance",
        accessor: "balance",
      },
      {
        Header: "Action",
        accessor: "action",
        Cell: ({ row }) => {
          const isBooking = row.original.party?.name === "Booking";
          return (
            <div className="flex justify-start md:justify-center items-center gap-5">

              <button
                onClick={() => handleEditPopup(row.original.id)}
                className={`p-2 flex items-center justify-center focus:outline-none w-[40px] h-[40px] rounded-full ${isBooking ? "opacity-50 cursor-not-allowed bg-gray-200 text-G300 " : "bg-green50 text-green "}`}
              >
                <icons.FaRegEdit className="text-xl font-bold" />
              </button>

              {editPopupIndex === row.original.id && (
                <EditExpenseForm
                  isOpen={editPopupIndex === row.original.id}
                  onClose={() => handleEditPopup(null)}
                  expenseId={row.original.id}
                  fetchExpenses={fetchExpenses}

                />
              )}
              <button
                onClick={() => handleDeletePopup(row.original.id)}
                className={`p-2  flex items-center justify-center focus:outline-none w-[40px] h-[40px] rounded-full ${isBooking ? "opacity-50 cursor-not-allowed bg-gray-200 text-G300 " : "bg-darkred bg-opacity-[5%] text-darkred "}`}
              >
                <icons.AiOutlineDelete className="text-xl font-bold" />
              </button>
              {deletePopupIndex === row.original.id && (
                <DeletePopup
                  isOpen={deletePopupIndex === row.original.id}
                  onClose={() => handleDeletePopup(null)}
                  fetchExpenses={fetchExpenses}
                  expenseId={row.original.id}
                />
              )}
            </div>
          )
        },
      },
    ],
    [editPopupIndex, deletePopupIndex]
  );

  const data = useMemo(() => {
    if (!Array.isArray(expense)) return [];
    return expense?.map((data) => ({

      id: data.id,
      date: data.date,
      time: data.time,
      amount: data.amount,
      details: data.details,
      balance: `₹ ${Math.trunc(data.balance)}`,
      entry_type: data.entry_type,
      payment_mode: data.payment_mode,
      party: data.party,
      expense_category: data.expense_category,
      entry_type: data.entry_type === "cash_out" ? "Cash Out" : " Cash In",
      files: data.files
    }));
  }, [expense]);

  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = data.slice(indexOfFirstRow, indexOfLastRow);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleRowsPerPageChange = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1);
  };

  return {
    columns,
    currentRows,
    totalRows: data.length,
    currentPage,
    rowsPerPage,
    handlePageChange,
    handleRowsPerPageChange,
    filters,
    setFilters,
    fetchExpenses,
    updateFilter,
    cashIn,
    cashOut,
    netBalance,
    clearFilters,
    loading
  };
};

export default useAddExpense;
